// AI Model Configuration for Smart Writer
// Comprehensive list of all available AI models from multiple providers

export interface AIModel {
  id: string;
  name: string;
  provider: 'openrouter' | 'openai' | 'anthropic' | 'google' | 'cohere' | 'mistral' | 'together' | 'replicate';
  category: 'gpt' | 'claude' | 'gemini' | 'command' | 'mistral' | 'llama' | 'other';
  maxTokens: number;
  costPer1kTokens: number; // in USD
  timeout: number; // in milliseconds
  retries: number;
  isRecommended?: boolean;
  description?: string;
}

export const AI_MODELS: AIModel[] = [
  // Microsoft WizardLM Model
  {
    id: 'microsoft/wizardlm-2-8x22b',
    name: 'WizardLM 2 8x22B',
    provider: 'openrouter',
    category: 'other',
    maxTokens: 4096,
    costPer1kTokens: 0.00063,
    timeout: 30000,
    retries: 2,
    description: 'Microsoft\'s instruction-tuned model'
  },
  
  // OpenAI GPT Models
  {
    id: 'openai/gpt-3.5-turbo-instruct',
    name: 'GPT-3.5 Turbo Instruct',
    provider: 'openrouter',
    category: 'gpt',
    maxTokens: 4096,
    costPer1kTokens: 0.0015,
    timeout: 25000,
    retries: 2,
    description: 'Instruction-tuned GPT-3.5'
  },
  {
    id: 'openai/gpt-3.5-turbo-16k',
    name: 'GPT-3.5 Turbo 16K',
    provider: 'openrouter',
    category: 'gpt',
    maxTokens: 16384,
    costPer1kTokens: 0.001,
    timeout: 25000,
    retries: 2,
    description: 'GPT-3.5 with extended context'
  },
  {
    id: 'openai/gpt-3.5-turbo',
    name: 'GPT-3.5 Turbo',
    provider: 'openrouter',
    category: 'gpt',
    maxTokens: 4096,
    costPer1kTokens: 0.0005,
    timeout: 20000,
    retries: 3,
    description: 'Fast and cost-effective'
  },

  // Mistral Models
  {
    id: 'mistralai/mixtral-8x7b-instruct',
    name: 'Mixtral 8x7B',
    provider: 'openrouter',
    category: 'mistral',
    maxTokens: 4096,
    costPer1kTokens: 0.00024,
    timeout: 25000,
    retries: 2,
    description: 'Mixture of experts model'
  },
  {
    id: 'mistralai/mistral-small',
    name: 'Mistral Small',
    provider: 'openrouter',
    category: 'mistral',
    maxTokens: 4096,
    costPer1kTokens: 0.0006,
    timeout: 25000,
    retries: 2,
    description: 'Fast Mistral model'
  },

  // Google Gemini Model
  {
    id: 'google/gemini-flash-1.5',
    name: 'Gemini Flash 1.5',
    provider: 'openrouter',
    category: 'gemini',
    maxTokens: 8192,
    costPer1kTokens: 0.000075,
    timeout: 20000,
    retries: 2,
    description: 'Fast Gemini model'
  },

  // Anthropic Claude Model
  {
    id: 'anthropic/claude-3.5-haiku',
    name: 'Claude 3.5 Haiku',
    provider: 'openrouter',
    category: 'claude',
    maxTokens: 8192,
    costPer1kTokens: 0.00025,
    timeout: 25000,
    retries: 2,
    description: 'Fast and efficient Claude model'
  }
];

// Fallback order for when models fail
export const FALLBACK_MODELS = [
  'openai/gpt-3.5-turbo',
  'anthropic/claude-3.5-haiku',
  'google/gemini-flash-1.5',
  'mistralai/mixtral-8x7b-instruct',
  'mistralai/mistral-small',
  'openai/gpt-3.5-turbo-16k',
  'openai/gpt-3.5-turbo-instruct',
  'microsoft/wizardlm-2-8x22b'
];

// Get model configuration by ID
export function getModelConfig(modelId: string): AIModel | undefined {
  return AI_MODELS.find(model => model.id === modelId);
}


// Get models by category
export function getModelsByCategory(category: string): AIModel[] {
  return AI_MODELS.filter(model => model.category === category);
}

// Get models by provider
export function getModelsByProvider(provider: string): AIModel[] {
  return AI_MODELS.filter(model => model.provider === provider);
}

// Get fast models (low timeout)
export function getFastModels(): AIModel[] {
  return AI_MODELS.filter(model => model.timeout <= 25000);
}

// Get cost-effective models
export function getCostEffectiveModels(): AIModel[] {
  return AI_MODELS.filter(model => model.costPer1kTokens <= 0.001);
}

// Get next fallback model
export function getNextFallbackModel(currentModelId: string): string | null {
  const currentIndex = FALLBACK_MODELS.indexOf(currentModelId);
  if (currentIndex === -1) {
    // If current model is not in fallback list, start with first fallback
    return FALLBACK_MODELS[0];
  }
  
  const nextIndex = currentIndex + 1;
  return nextIndex < FALLBACK_MODELS.length ? FALLBACK_MODELS[nextIndex] : null;
}

// Model categories for UI grouping
export const MODEL_CATEGORIES = {
  gpt: 'OpenAI GPT',
  claude: 'Anthropic Claude',
  gemini: 'Google Gemini',
  llama: 'Meta Llama',
  mistral: 'Mistral AI',
  command: 'Cohere Command',
  other: 'Other Models'
};